import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Building2, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import RestaurantManagerAssignment from '@/components/RestaurantManagerAssignment';
import { useAuth } from '@/contexts/AuthContext';
import { useRestaurantOnboarding } from '@/hooks/useRestaurantOnboarding';
import { draftFromSession, restaurantDraftPayload, validateRestaurantDraft } from '@/lib/restaurantOnboardingDraft';

const errorKeys = {
  authenticationExpired: 'restaurantOnboarding.authenticationExpired',
  permissionDenied: 'restaurantOnboarding.permissionDenied',
  validationError: 'restaurantOnboarding.validationError',
  temporaryError: 'restaurantOnboarding.temporaryError',
  genericError: 'restaurantOnboarding.genericError',
};

const completionErrorKeys = {
  authenticationExpired: 'restaurantOnboarding.authenticationExpired',
  permissionDenied: 'restaurantOnboarding.permissionDenied',
  validationError: 'restaurantOnboarding.validationError',
  completionNotReady: 'restaurantOnboarding.completionNotReady',
  temporaryError: 'restaurantOnboarding.completionTemporaryError',
  genericError: 'restaurantOnboarding.completionGenericError',
};

export default function RestaurantOnboarding() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const {
    phase,
    session,
    errorKey,
    saveErrorKey,
    completionErrorKey,
    isSaving,
    isCompleting,
    completion,
    conflictDraft,
    retry,
    recover,
    saveDraft,
    completeOnboarding,
    checkCompletionStatus,
    dismissConflict,
  } = useRestaurantOnboarding(user);

  let content;
  if (phase === 'idle' || phase === 'loading') {
    content = (
      <div className="flex items-center gap-3 text-muted-foreground" role="status">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('restaurantOnboarding.loading')}
      </div>
    );
  } else if (phase === 'stale') {
    content = (
      <Status icon={AlertCircle} title={t('restaurantOnboarding.sessionUnavailable')}>
        <p>{t('restaurantOnboarding.staleSession')}</p>
        <Button onClick={recover}>
          {t('restaurantOnboarding.recover')}
        </Button>
      </Status>
    );
  } else if (phase === 'staleMissingRequest') {
    content = (
      <Status icon={AlertCircle} title={t('restaurantOnboarding.sessionUnavailable')}>
        <p>{t('restaurantOnboarding.staleMissingRequest')}</p>
      </Status>
    );
  } else if (phase === 'error') {
    content = (
      <Status icon={AlertCircle} title={t('restaurantOnboarding.sessionUnavailable')}>
        <p>{t(errorKeys[errorKey] || errorKeys.genericError)}</p>
        <Button onClick={retry}>
          {t('restaurantOnboarding.retry')}
        </Button>
      </Status>
    );
  } else {
    content = (
      <>
        <Status
          icon={phase === 'completionBlocked'
            ? AlertCircle
            : phase === 'completionPending'
              ? Loader2
              : CheckCircle2}
          title={statusTitle(phase, t)}
        >
          <p>{statusDescription(phase, t)}</p>
        </Status>

        {session?.company?.name && (
          <CompanySummary company={session.company} t={t} />
        )}

        {phase === 'ready' && (
          <>
            <RestaurantDraftForm
              session={session}
              isSaving={isSaving}
              saveErrorKey={saveErrorKey}
              conflictDraft={conflictDraft}
              onSave={saveDraft}
              onDismissConflict={dismissConflict}
              onDirtyChange={setHasUnsavedChanges}
              t={t}
            />

            <RestaurantReview
              session={session}
              completionErrorKey={completionErrorKey}
              isCompleting={isCompleting}
              hasUnsavedChanges={hasUnsavedChanges}
              onComplete={completeOnboarding}
              t={t}
            />
          </>
        )}

        {phase === 'completionPending' && (
          <CompletionPending
            completionErrorKey={completionErrorKey}
            onCheckStatus={checkCompletionStatus}
            t={t}
          />
        )}

        {phase === 'completionRetryable' && (
          <CompletionRetryable
            session={session}
            completionErrorKey={completionErrorKey}
            isCompleting={isCompleting}
            onRetry={completeOnboarding}
            onCheckStatus={checkCompletionStatus}
            t={t}
          />
        )}

        {phase === 'completionBlocked' && (
          <CompletionBlocked
            session={session}
            completionErrorKey={completionErrorKey}
            onCheckStatus={checkCompletionStatus}
            t={t}
          />
        )}

        {phase === 'completed' && (
          <>
            <CompletedSummary
              session={session}
              t={t}
            />
            <RestaurantManagerAssignment
              completion={completion}
              restaurantName={completion?.restaurant?.name || session?.restaurant?.name}
              user={user}
              t={t}
            />
          </>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid="restaurant-onboarding-page">
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl">
            {t('restaurantOnboarding.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {content}
        </CardContent>
      </Card>
    </div>
  );
}

function RestaurantDraftForm({ session, isSaving, saveErrorKey, conflictDraft, onSave, onDismissConflict, onDirtyChange, t }) {
  const [draft, setDraft] = useState(() => draftFromSession(session));
  const [validationKey, setValidationKey] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(draftFromSession(session));
    setSaved(false);
  }, [session]);

  const authoritativeDraft = draftFromSession(session);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(authoritativeDraft);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setValidationKey(null);
    setSaved(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextValidationKey = validateRestaurantDraft(draft);
    if (nextValidationKey) {
      setValidationKey(nextValidationKey);
      return;
    }
    const success = await onSave(restaurantDraftPayload(draft));
    setSaved(success);
  };

  const messageKey = validationKey || saveErrorKey;
  const restorePreviousValues = () => {
    if (!conflictDraft) return;
    setDraft(draftFromSession({ restaurant: conflictDraft }));
    setValidationKey(null);
    setSaved(false);
    onDismissConflict();
  };

  return (
    <form className="space-y-5 rounded-lg border p-4" onSubmit={handleSubmit} noValidate>
      <div>
        <h2 className="font-semibold">
          {t('restaurantOnboarding.restaurantDetails')}
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          {t('restaurantOnboarding.draftHelp')}
        </p>
      </div>

      {conflictDraft && (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950" role="alert">
          <div>
            <p className="font-semibold">
              {t('restaurantOnboarding.revisionConflictTitle')}
            </p>

            <p className="mt-1">
              {t('restaurantOnboarding.latestVersionLoaded')}
            </p>

            <p className="mt-1">
              {t('restaurantOnboarding.previousValuesPreserved')}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={restorePreviousValues}>
              {t('restaurantOnboarding.restorePreviousValues')}
            </Button>

            <Button type="button" variant="ghost" onClick={onDismissConflict}>
              {t('restaurantOnboarding.dismissConflict')}
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <DraftField id="onboarding-name" label={t('restaurantOnboarding.restaurantName')} value={draft.name} onChange={(value) => update('name', value)} maxLength={160} />

        <DraftField id="onboarding-currency" label={t('restaurantOnboarding.currency')} value={draft.currency} onChange={(value) => update('currency', value.toUpperCase())} maxLength={3} placeholder="USD" />

        <DraftField id="onboarding-phone" label={t('restaurantOnboarding.phone')} value={draft.phone} onChange={(value) => update('phone', value)} maxLength={40} />

        <DraftField id="onboarding-whatsapp" label={t('restaurantOnboarding.whatsappNumber')} value={draft.whatsapp_number} onChange={(value) => update('whatsapp_number', value)} maxLength={40} />
      </div>

      <WhatsAppReadiness number={draft.whatsapp_number} t={t} />

      <DraftField id="onboarding-address" label={t('restaurantOnboarding.address')} value={draft.address} onChange={(value) => update('address', value)} maxLength={500} />

      <div className="flex items-center justify-between gap-4 rounded-md border p-3">
        <Label htmlFor="onboarding-tax-enabled">
          {t('restaurantOnboarding.taxEnabled')}
        </Label>

        <Switch id="onboarding-tax-enabled" checked={draft.tax_enabled} onCheckedChange={(checked) => update('tax_enabled', checked)} />
      </div>

      <DraftField id="onboarding-tax-rate" label={t('restaurantOnboarding.taxRate')} value={draft.tax_rate} onChange={(value) => update('tax_rate', value)} type="number" min="0" max="100" disabled={!draft.tax_enabled} />

      {messageKey && (
        <p className="text-sm text-destructive" role="alert">
          {t(`restaurantOnboarding.${messageKey}`)}
        </p>
      )}

      {saved && (
        <p className="text-sm text-emerald-700" role="status">
          {t('restaurantOnboarding.saved')}
        </p>
      )}

      <Button type="submit" disabled={isSaving}>
        {isSaving ? t('restaurantOnboarding.saving') : t('restaurantOnboarding.saveDraft')}
      </Button>
    </form>
  );
}

function RestaurantReview({
  session,
  completionErrorKey,
  isCompleting,
  hasUnsavedChanges,
  onComplete,
  t,
}) {
  const restaurant = session?.restaurant;
  const savedDraft = draftFromSession(session);
  const savedDraftForValidation = {
    ...savedDraft,
    name: savedDraft.name ?? '',
    address: savedDraft.address ?? '',
    phone: savedDraft.phone ?? '',
    whatsapp_number: savedDraft.whatsapp_number ?? '',
    currency: savedDraft.currency ?? '',
    tax_rate: savedDraft.tax_rate ?? '0',
  };
  const savedDraftReady = Boolean(
    savedDraftForValidation.name.trim()
    && savedDraftForValidation.currency.trim()
    && !validateRestaurantDraft(savedDraftForValidation)
  );

  if (!restaurant) return null;

  return (
    <section
      className="space-y-5 rounded-lg border p-4"
      aria-labelledby="restaurant-onboarding-review"
    >
      <div>
        <h2
          id="restaurant-onboarding-review"
          className="font-semibold"
        >
          {t('restaurantOnboarding.reviewTitle')}
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          {t('restaurantOnboarding.reviewHelp')}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ReviewItem
          label={t('restaurantOnboarding.restaurantName')}
          value={restaurant.name}
        />

        <ReviewItem
          label={t('restaurantOnboarding.currency')}
          value={restaurant.currency}
        />

        <ReviewItem
          label={t('restaurantOnboarding.phone')}
          value={restaurant.phone}
          emptyLabel={t('restaurantOnboarding.notProvided')}
        />

        <ReviewItem
          label={t('restaurantOnboarding.whatsappNumber')}
          value={restaurant.whatsapp_number}
          emptyLabel={t('restaurantOnboarding.notProvided')}
        />

        <ReviewItem
          label={t('restaurantOnboarding.address')}
          value={restaurant.address}
          emptyLabel={t('restaurantOnboarding.notProvided')}
        />

        <ReviewItem
          label={t('restaurantOnboarding.taxEnabled')}
          value={
            restaurant.tax_enabled
              ? t('restaurantOnboarding.yes')
              : t('restaurantOnboarding.no')
          }
        />

        {restaurant.tax_enabled && (
          <ReviewItem
            label={t('restaurantOnboarding.taxRate')}
            value={`${restaurant.tax_rate ?? 0}%`}
          />
        )}
      </div>

      <WhatsAppReview
        number={restaurant.whatsapp_number}
        t={t}
      />

      <div className="rounded-md border bg-muted/30 p-4 text-sm">
        <p className="font-medium">
          {t('restaurantOnboarding.completionNoticeTitle')}
        </p>

        <p className="mt-1 text-muted-foreground">
          {t('restaurantOnboarding.completionNotice')}
        </p>
      </div>

      {hasUnsavedChanges && (
        <p className="text-sm text-destructive" role="alert">
          {t('restaurantOnboarding.unsavedChangesBeforeCreate')}
        </p>
      )}

      {!hasUnsavedChanges && !savedDraftReady && (
        <p className="text-sm text-destructive" role="alert">
          {t('restaurantOnboarding.savedDraftNotReady')}
        </p>
      )}

      {completionErrorKey && (
        <p
          className="text-sm text-destructive"
          role="alert"
        >
          {t(
            completionErrorKeys[completionErrorKey]
            || completionErrorKeys.genericError
          )}
        </p>
      )}

      <Button
        type="button"
        disabled={isCompleting || hasUnsavedChanges || !savedDraftReady}
        onClick={onComplete}
      >
        {isCompleting ? (
          <>
            <Loader2 className="me-2 h-4 w-4 animate-spin" />
            {t('restaurantOnboarding.creatingRestaurant')}
          </>
        ) : (
          t('restaurantOnboarding.createRestaurant')
        )}
      </Button>
    </section>
  );
}

function CompletionPending({
  completionErrorKey,
  onCheckStatus,
  t,
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div>
        <h2 className="font-semibold">
          {t('restaurantOnboarding.completionPendingTitle')}
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          {t('restaurantOnboarding.completionPending')}
        </p>
      </div>

      {completionErrorKey && (
        <p
          className="text-sm text-destructive"
          role="alert"
        >
          {t(
            completionErrorKeys[completionErrorKey]
            || completionErrorKeys.genericError
          )}
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={onCheckStatus}
      >
        {t('restaurantOnboarding.checkStatus')}
      </Button>
    </section>
  );
}

function CompletionRetryable({
  session,
  completionErrorKey,
  isCompleting,
  onRetry,
  onCheckStatus,
  t,
}) {
  return (
    <section className="space-y-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div>
        <h2 className="font-semibold text-amber-950">
          {t('restaurantOnboarding.completionRetryableTitle')}
        </h2>

        <p className="mt-1 text-sm text-amber-900">
          {t('restaurantOnboarding.completionRetryable')}
        </p>
      </div>

      {session?.completion_error_code && (
        <p className="text-sm text-amber-900">
          {t('restaurantOnboarding.completionReference', {
            code: session.completion_error_code,
          })}
        </p>
      )}

      {completionErrorKey && (
        <p
          className="text-sm text-destructive"
          role="alert"
        >
          {t(
            completionErrorKeys[completionErrorKey]
            || completionErrorKeys.genericError
          )}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={isCompleting}
          onClick={onRetry}
        >
          {isCompleting
            ? t('restaurantOnboarding.creatingRestaurant')
            : t('restaurantOnboarding.retryCompletion')}
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={isCompleting}
          onClick={onCheckStatus}
        >
          {t('restaurantOnboarding.checkStatus')}
        </Button>
      </div>
    </section>
  );
}

function CompletionBlocked({
  session,
  completionErrorKey,
  onCheckStatus,
  t,
}) {
  return (
    <section
      className="space-y-4 rounded-lg border border-destructive/40 p-4"
      role="alert"
    >
      <div>
        <h2 className="font-semibold">
          {t('restaurantOnboarding.completionBlockedTitle')}
        </h2>

        <p className="mt-1 text-sm text-muted-foreground">
          {t('restaurantOnboarding.completionBlocked')}
        </p>
      </div>

      {session?.completion_error_code && (
        <p className="text-sm text-muted-foreground">
          {t('restaurantOnboarding.completionReference', {
            code: session.completion_error_code,
          })}
        </p>
      )}

      {completionErrorKey && (
        <p className="text-sm text-destructive">
          {t(
            completionErrorKeys[completionErrorKey]
            || completionErrorKeys.genericError
          )}
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={onCheckStatus}
      >
        {t('restaurantOnboarding.checkStatus')}
      </Button>
    </section>
  );
}

function CompletedSummary({
  session,
  t,
}) {
  const restaurant = session?.restaurant;

  return (
    <section
      className="space-y-4 rounded-lg border border-emerald-300 bg-emerald-50 p-4"
      aria-labelledby="restaurant-onboarding-completed"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-700" />

        <h2
          id="restaurant-onboarding-completed"
          className="font-semibold text-emerald-950"
        >
          {t('restaurantOnboarding.completedSummaryTitle')}
        </h2>
      </div>

      <p className="text-sm text-emerald-900">
        {t('restaurantOnboarding.completedSummary')}
      </p>

      {restaurant && (
        <div className="grid gap-4 rounded-md border border-emerald-200 bg-white/60 p-4 md:grid-cols-2">
          <ReviewItem
            label={t('restaurantOnboarding.restaurantName')}
            value={restaurant.name}
          />

          <ReviewItem
            label={t('restaurantOnboarding.currency')}
            value={restaurant.currency}
          />

          <ReviewItem
            label={t('restaurantOnboarding.phone')}
            value={restaurant.phone}
            emptyLabel={t('restaurantOnboarding.notProvided')}
          />

          <ReviewItem
            label={t('restaurantOnboarding.whatsappNumber')}
            value={restaurant.whatsapp_number}
            emptyLabel={t('restaurantOnboarding.notProvided')}
          />
        </div>
      )}

    </section>
  );
}

function WhatsAppReadiness({
  number,
  t,
}) {
  return (
    <section
      className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4" aria-labelledby="onboarding-whatsapp-readiness">
      <h3 id="onboarding-whatsapp-readiness" className="font-semibold">
        {t('restaurantOnboarding.whatsappReadiness')}
      </h3>

      {number ? (
        <>
          <p className="text-sm text-slate-700">
            <span className="font-medium">
              {t('restaurantOnboarding.whatsappNumberSaved')}:
            </span>{' '}
            {number}
          </p>

          <p className="text-sm text-muted-foreground">
            {t('restaurantOnboarding.whatsappConfigurationPending')}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('restaurantOnboarding.whatsappNumberMissing')}
        </p>
      )}

      <p className="text-sm text-muted-foreground">
        {t('restaurantOnboarding.whatsappNoProvisioning')}
      </p>
    </section>
  );
}

function WhatsAppReview({
  number,
  t,
}) {
  return (
    <section className="space-y-2 rounded-md border bg-muted/20 p-4">
      <h3 className="font-semibold">
        {t('restaurantOnboarding.whatsappReadiness')}
      </h3>

      {number ? (
        <>
          <p className="text-sm">
            {t('restaurantOnboarding.whatsappReviewSaved', {
              number,
            })}
          </p>

          <p className="text-sm text-muted-foreground">
            {t(
              'restaurantOnboarding.whatsappReviewNotConnected'
            )}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('restaurantOnboarding.whatsappReviewMissing')}
        </p>
      )}
    </section>
  );
}

function ReviewItem({
  label,
  value,
  emptyLabel = '',
}) {
  const hasValue =
    value !== null
    && value !== undefined
    && value !== '';

  return (
    <div>
      <p className="text-sm text-muted-foreground">
        {label}
      </p>

      <p className="mt-1 font-medium">
        {hasValue ? String(value) : emptyLabel}
      </p>
    </div>
  );
}

function DraftField({
  id,
  label,
  value,
  onChange,
  ...inputProps
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
      </Label>

      <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} {...inputProps} />
    </div>
  );
}

function Status({ icon: Icon, title, children }) {
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center gap-2 font-semibold">
        <Icon className="h-5 w-5" />
        {title}
      </div>

      <div className="space-y-3 text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

function CompanySummary({ company, t }) {
  return (
    <section className="rounded-lg border bg-muted/30 p-4" aria-labelledby="onboarding-company-heading">
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-[#E57200]" />

        <h2 id="onboarding-company-heading" className="font-semibold">
          {t('restaurantOnboarding.companyConfirmation')}
        </h2>
      </div>

      <p className="mt-3 text-sm text-muted-foreground">
        {t('restaurantOnboarding.company')}
      </p>

      <p className="mt-1 font-medium">
        {company.name}
      </p>

      {company.subscription_plan && (
        <p className="mt-2 text-sm text-muted-foreground">
          {t('restaurantOnboarding.subscriptionPlan', { plan: company.subscription_plan })}
        </p>
      )}
    </section>
  );
}

function statusTitle(phase, t) {
  if (phase === 'completed') {
    return t('restaurantOnboarding.completedTitle');
  }

  if (phase === 'completionPending') {
    return t('restaurantOnboarding.completionPendingTitle');
  }

  if (phase === 'completionRetryable') {
    return t('restaurantOnboarding.completionRetryableTitle');
  }

  if (phase === 'completionBlocked') {
    return t('restaurantOnboarding.completionBlockedTitle');
  }

  return t('restaurantOnboarding.sessionReady');
}

function statusDescription(phase, t) {
  if (phase === 'completed') {
    return t('restaurantOnboarding.completed');
  }

  if (phase === 'completionPending') {
    return t('restaurantOnboarding.completionPending');
  }

  if (phase === 'completionRetryable') {
    return t('restaurantOnboarding.completionRetryable');
  }

  if (phase === 'completionBlocked') {
    return t('restaurantOnboarding.completionBlocked');
  }

  return t('restaurantOnboarding.foundationReady');
}
